import React, { useCallback, useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import useWidgetFilterNavigator, {
  FilterCondition,
} from "../../../../useWidgetFilterNavigator";
import TopFeatureTable, {
  BaseTopFeatureRow,
  WidgetTableProps,
} from "../TopFeatureTable";
import { GET_TOP_FIXES_DATA } from "../../../../WidgetDataApi";
import useQueryWithRetries from "../../../../../Tools/useQueryWithRetries";
import {
  Tooltip,
  TableCell,
  tableCellTypesEnum,
  formatNumber,
} from "@SeemplicityDev/shared";

interface TopFixRow extends BaseTopFeatureRow {
  fix: string;
  resources: number;
}

const columnHelper = createColumnHelper<TopFixRow>();

const TopFixesTable: React.FC<WidgetTableProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  id,
  isForExport = false,
  setTotalWidgets = null,
  index = null,
  setIsReadyToExport = null,
}) => {
  const { data, error, loading, refetch } = useQueryWithRetries({
    query: GET_TOP_FIXES_DATA,

    variables: {
      widget_definition_key: "top_10_fixes",
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: scopeToExecute,
    scopeId: selectedScope?.value?.id ?? null,
  });

  const onChartClick = useCallback(
    (severity: string, { fix }: TopFixRow) => {
      const filters: FilterCondition[] = [["in", "severity", severity]];
      if (fix.trim()) {
        filters.push(["likelist", "title", fix]);
      }
      onFilterClick(filters);
    },
    [onFilterClick]
  );

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("fix", {
        cell: (props) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <Tooltip content={props.getValue()}>
              <span className="fix-cell">{props.getValue()}</span>
            </Tooltip>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Fix",
        minSize: 220,
      }),
      columnHelper.accessor("resources", {
        cell: (props) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <span className="resources-cell">
              {formatNumber(props.getValue())}
            </span>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Resources",
      }),
    ];
  }, []);

  return (
    <TopFeatureTable
      columns={columns}
      data={data}
      error={error}
      loading={loading}
      refetch={refetch}
      onChartClick={onChartClick}
      id={id}
      isForExport={isForExport}
      setTotalWidgets={setTotalWidgets}
      index={index}
      identifier={"fix"}
      setIsReadyToExport={setIsReadyToExport}
    />
  );
};

export default TopFixesTable;
